import React, { useEffect, useState } from 'react';
import { hqStaffService, HqStaff } from '../services/hqStaffService';
import { hqAuthService } from '../services/hqAuthService';

const HqStaffPage: React.FC = () => {
  const me = hqAuthService.getCachedUser();
  const isAdmin = me?.role === 'hq_admin';

  const [staff, setStaff] = useState<HqStaff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'hq_staff' as 'hq_admin' | 'hq_staff' });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setStaff(await hqStaffService.list());
    } catch (e: any) {
      setError(e?.response?.data?.detail || '직원 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await hqStaffService.create(form);
      setShowAdd(false);
      setForm({ email: '', password: '', name: '', role: 'hq_staff' });
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || '직원 추가 실패');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (s: HqStaff) => {
    if (s.id === me?.user_id) {
      alert('본인 계정은 삭제할 수 없습니다.');
      return;
    }
    if (!window.confirm(`${s.name}(${s.email}) 계정을 삭제하시겠습니까?`)) return;
    try {
      await hqStaffService.delete(s.id);
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || '삭제 실패');
    }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-800">직원 관리</h1>
        {isAdmin && (
          <button
            onClick={() => setShowAdd(true)}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg"
          >
            + 직원 추가
          </button>
        )}
      </div>

      {error && (
        <div className="px-4 py-3 mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <table className="w-full min-w-[680px]">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">이름</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">이메일</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">권한</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">가입일</th>
              {isAdmin && <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">작업</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={isAdmin ? 5 : 4} className="px-4 py-8 text-center text-slate-400">
                  불러오는 중...
                </td>
              </tr>
            ) : staff.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 5 : 4} className="px-4 py-8 text-center text-slate-400">
                  등록된 직원이 없습니다.
                </td>
              </tr>
            ) : (
              staff.map((s) => (
                <tr key={s.id} className="border-b last:border-b-0 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    {s.name}
                    {s.id === me?.user_id && (
                      <span className="ml-2 text-xs text-blue-600">(나)</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{s.email}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                        s.role === 'hq_admin'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {s.role === 'hq_admin' ? '관리자' : '일반'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-sm">
                    {s.created_at ? new Date(s.created_at).toLocaleDateString('ko-KR') : '-'}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDelete(s)}
                        disabled={s.id === me?.user_id}
                        className="text-sm text-red-600 hover:text-red-800 disabled:text-slate-300 disabled:cursor-not-allowed"
                      >
                        삭제
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 직원 추가 모달 */}
      {showAdd && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <form onSubmit={handleAdd} className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">직원 추가</h2>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">이름</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">이메일</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  비밀번호 <span className="text-xs text-slate-400">(8자 이상)</span>
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  minLength={8}
                  required
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">권한</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value as any })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="hq_staff">일반 직원 (조회만)</option>
                  <option value="hq_admin">관리자 (모든 권한)</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowAdd(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg disabled:bg-slate-400"
              >
                {submitting ? '추가 중...' : '추가'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default HqStaffPage;
